import { FC } from "react";

import { Card, HStack, Text, VStack } from "@/components/ui";

import { formatMargin, PARTY_COLOR, TOSSUP } from "./colors";
import InfoTip from "./InfoTip";
import { SeatStrip } from "./Overview";
import type { Office } from "./types";
import type { ResultsTally } from "./view";

interface ResultsOverviewProps {
  /** National House vote, Foresight's and the count's (House only). */
  generic: { count: number | null; foresight: number | null } | null;
  office: Office;
  tally: ResultsTally;
}

/* ── Constants ─────────────────────────────────── */

const HINT =
  "Our call is the side Foresight's read favored, however narrowly. A race is scored once it's called. Margins compare Foresight's two-party margin with the count's, so they move as votes come in. Counts and calls: civicAPI, refreshed every minute.";

const MISSES_SHOWN = 8;

const SHAPE: Record<
  Office,
  { marker: number; noun: string; seats: string; title: string; total: number }
> = {
  governor: {
    marker: 25,
    noun: "races",
    seats: "36 races · 14 governors not up",
    title: "Governors",
    total: 50,
  },
  house: {
    marker: 217.5,
    noun: "seats",
    seats: "435 seats · 218 for control",
    title: "House",
    total: 435,
  },
  senate: {
    marker: 50,
    noun: "races",
    seats: "35 races · 65 senators not up",
    title: "Senate",
    total: 100,
  },
};

/* ── Component ─────────────────────────────────── */

/** How Foresight's calls fared against the count, for one office. */
const ResultsOverview: FC<ResultsOverviewProps> = ({
  generic,
  office,
  tally,
}) => {
  const shape = SHAPE[office];
  const { actual, judged, measured, misses, ours, republicanGap } = tally;
  const missed =
    misses.length > MISSES_SHOWN
      ? `${misses.slice(0, MISSES_SHOWN).join(", ")} +${misses.length - MISSES_SHOWN} more`
      : misses.join(", ");

  return (
    <Card className="p-5 rounded-2xl border border-outline-200">
      <VStack className="gap-3">
        <HStack className="items-baseline justify-between flex-wrap gap-2">
          <HStack className="items-center gap-1">
            <Text className="text-sm font-semibold text-typography-900">
              {shape.title} · our calls vs the count
            </Text>
            <InfoTip text={HINT} />
          </HStack>
          <Text className="text-xs text-typography-500">{shape.seats}</Text>
        </HStack>
        <SeatStrip
          marker={shape.marker}
          segments={[
            { color: PARTY_COLOR.D, key: "D", value: actual.D },
            { color: PARTY_COLOR.I, key: "I", value: actual.I },
            { color: TOSSUP, key: "open", value: tally.uncalled },
            { color: PARTY_COLOR.R, key: "R", value: actual.R },
          ]}
          total={shape.total}
        />
        <HStack className="justify-between">
          <Text className="text-sm font-semibold text-typography-900">
            D {actual.D}
            {actual.I > 0 ? ` · I ${actual.I}` : ""}
          </Text>
          <Text className="text-xs text-typography-500">
            {tally.uncalled} not called yet
          </Text>
          <Text className="text-sm font-semibold text-typography-900">
            R {actual.R}
          </Text>
        </HStack>
        <VStack className="gap-1">
          <Text className="text-sm text-typography-700">
            Our calls: D {ours.D}
            {ours.I > 0 ? ` · I ${ours.I}` : ""} · R {ours.R}
            {tally.unread > 0 ? ` · ${tally.unread} without a read` : ""}
          </Text>
          <Text className="text-sm text-typography-700">
            {judged > 0
              ? `Right on ${judged - misses.length} of ${judged} called ${shape.noun}${misses.length > 0 ? `; missed ${missed}` : ""}.`
              : `No ${shape.noun} called yet.`}
          </Text>
          {republicanGap !== null && (
            <Text className="text-sm text-typography-700">
              On average our margins were {Math.abs(republicanGap).toFixed(1)}{" "}
              pts {republicanGap > 0 ? "more" : "less"} Republican than the
              count ({measured} {shape.noun} with votes counted).
            </Text>
          )}
          {generic && (
            <Text className="text-sm text-typography-700">
              National House vote: Foresight {formatMargin(generic.foresight)} ·
              count {formatMargin(generic.count)}.
            </Text>
          )}
        </VStack>
      </VStack>
    </Card>
  );
};

export default ResultsOverview;
